import * as React from "react";
import {
    Badge,
    BadgeProps,
    Box,
    Container,
    DialogContent,
    Grid,
    Typography,
} from "@mui/material";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { styled } from "@mui/material/styles";
import {
    DialogButton,
    DialogState,
    useDialogState,
} from "../common/DialogButton";
import { StripeCheckout } from "./StripeCheckout";
import { LocalCurrency } from "../common/LocalCurrency";
import { constNull, pipe } from "fp-ts/function";
import { array, nonEmptyArray, option } from "fp-ts";
import { CartItem, PaymentMethod } from "../../domain/payment";
import { assertUnreachable } from "../../utils/assertUnreachable";
import { TransferCheckout } from "./TransferCheckout";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { sequenceS } from "fp-ts/Apply";
import { CancelableDialogBody } from "../user/CancelableDialogBody";

interface Props {
    items: Array<CartItem>;
    eventId: number;
    paymentMethod: Nullable<PaymentMethod>;
}

export const Cart = (p: Props) => {
    const dialogState = useDialogState();

    return pipe(
        {
            items: nonEmptyArray.fromArray(p.items),
            paymentMethod: option.fromNullable(p.paymentMethod),
        },
        sequenceS(option.Apply),
        option.fold(constNull, (ctx) => (
            <Box
                sx={{
                    float: "right",
                }}
            >
                <StyledBadge badgeContent={ctx.items.length} color="error">
                    <DialogButton
                        state={dialogState}
                        btn={{
                            children: "zaplatit",
                            variant: "contained",
                            startIcon: <ShoppingCartCheckoutIcon />,
                        }}
                        dialog={{
                            children: (
                                <CheckoutDialog
                                    items={ctx.items}
                                    dialogState={dialogState}
                                    eventId={p.eventId}
                                    paymentMethod={ctx.paymentMethod}
                                />
                            ),
                            fullScreen: true,
                        }}
                    />
                </StyledBadge>
            </Box>
        ))
    );
};

interface CheckoutDialogProps {
    dialogState: DialogState;
    items: NonEmptyArray<CartItem>;
    eventId: number;
    paymentMethod: PaymentMethod;
}

const CheckoutDialog = (p: CheckoutDialogProps) => {
    const amount = pipe(
        p.items,
        array.reduce(0, (acc, item) => item.price + acc)
    );

    return (
        <DialogContent>
            <Container maxWidth={"sm"}>
                <Grid direction={"column"} container>
                    <Grid item>
                        <Typography variant={"caption"} component={"div"}>
                            celkem k úhradě
                        </Typography>
                        <Typography variant={"h2"} component={"div"}>
                            <LocalCurrency value={amount} currency={"czk"} />
                        </Typography>
                        <ul>
                            {pipe(
                                p.items,
                                array.map((item) => (
                                    <li key={item.id}>
                                        <strong>{item.name}</strong> {item.born}
                                        {" - "}
                                        {item.race}{" "}
                                        <strong>
                                            <LocalCurrency
                                                value={item.price}
                                                currency={"czk"}
                                            />
                                        </strong>
                                    </li>
                                ))
                            )}
                        </ul>
                    </Grid>
                    <Grid item>
                        {pipe(p.paymentMethod, (method) => {
                            switch (method.kind) {
                                case "stripe":
                                    return (
                                        <StripeCheckout
                                            eventId={p.eventId}
                                            amount={amount}
                                            items={p.items}
                                        />
                                    );

                                case "transfer":
                                    return (
                                        <CancelableDialogBody
                                            onCancel={p.dialogState.close}
                                            cancelBtnLabel={"zpět"}
                                        >
                                            <TransferCheckout
                                                transferMethod={method}
                                                amount={amount}
                                                items={p.items}
                                                eventId={p.eventId}
                                            />
                                        </CancelableDialogBody>
                                    );
                            }

                            assertUnreachable(method);
                        })}
                    </Grid>
                </Grid>
            </Container>
        </DialogContent>
    );
};

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    "& .MuiBadge-badge": {
        right: -3,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: "0 4px",
    },
}));
